/**
 * 操作日志「人话化」· 把审计中间件写的原始 HTTP 流水 summary
 * （如 `PATCH /api/v1/vehicles/{uuid}/status`）转成中文可读（如「修改车辆状态」）。
 *
 * 背景：AuditMiddleware 对所有写请求自动写 op_audit_logs，summary = "{METHOD} {path}"（原始）。
 * 语义审计（路由内显式 audit_log()）写的 summary 已是中文 → 原样返回。
 * 一处转换，所有操作日志渲染处复用（车辆/订单/产品/酒店…）。
 */

const RESOURCE_CN: Record<string, string> = {
  orders: "订单", vehicles: "车辆", products: "产品", hotels: "酒店",
  "hotel-price-policies": "酒店价格", attractions: "门票",
  "attraction-price-policies": "门票价格", drivers: "司导", suppliers: "供应商",
  customers: "客户", "channel-partners": "同业客户", "self-pay": "自费",
  pricing: "定价", finance: "财务", invoices: "发票", dispatch: "调度",
  "vehicle-operations": "车务", uploads: "附件", complaints: "投诉",
};

const ACTION_CN: Record<string, string> = {
  status: "状态", confirm: "确认", cancel: "取消", "assign-driver": "派司导",
  voucher: "凭证", "issue-voucher": "签发凭证", schedules: "排期",
  "price-policies": "价格策略", policies: "价格策略", inventory: "库存",
  reassign: "改派", approve: "审批", reject: "驳回", notes: "备注",
  confirm_payment: "确认收款", "mark-paid": "确认收款", refund: "退款",
  escalate: "升级特批", "ticket-types": "票种", overrides: "单日改价",
  licenses: "证照", maintenance: "维保", "seat-layout": "座位布局",
  rooms: "房型", contacts: "联系人", "rotate-key": "轮换密钥",
  // TAOTUAN-2c 调度操作
  transfer: "转团", upgrade: "升级车档", hand_off: "转同行", "add-vehicle": "加车", "swap-vehicle": "换车",
  "move-run": "移车次", "merge-segment": "并车",
};

const METHOD_CN: Record<string, string> = {
  POST: "新建", PATCH: "修改", PUT: "更新", DELETE: "删除", GET: "查看",
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HTTP_RE = /^(GET|POST|PATCH|PUT|DELETE)\s+(\/\S+)/;

/** 原始 HTTP summary → 中文；已是中文语义 summary → 原样返回。 */
export function humanizeAuditSummary(summary: string | null | undefined): string {
  if (!summary) return "—";
  const m = summary.match(HTTP_RE);
  if (!m) return summary; // 已是中文语义 summary

  const method = m[1];
  const segs = m[2].split("/").filter((p) => p && p !== "api" && !/^v\d+$/.test(p) && !UUID_RE.test(p));
  const resource = segs[0] ?? "";
  const tail = segs.length > 1 ? segs[segs.length - 1] : null;

  const rcn = RESOURCE_CN[resource] ?? resource;
  const mcn = METHOD_CN[method] ?? method;

  if (tail) {
    const acn = ACTION_CN[tail] ?? tail;
    // 「修改车辆状态」「修改产品排期」「确认订单」(confirm/cancel 等动作型 tail 直接动词)
    if (["confirm", "cancel", "approve", "reject", "assign-driver", "reassign", "escalate", "mark-paid", "confirm_payment", "rotate-key"].includes(tail)) {
      return `${acn}${rcn}`;
    }
    return `${mcn}${rcn}${acn}`;
  }
  if (method === "POST") return `新建${rcn}`;
  if (method === "DELETE") return `删除${rcn}`;
  return `${mcn}${rcn}`;
}
